import React, { useState, useRef, useEffect } from 'react';
import { Input } from './input';
import { cn } from '@/lib/utils';

interface OTPInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  className?: string;
  dir?: 'ltr' | 'rtl';
  autoFocus?: boolean;
  onComplete?: () => void;
}

export function OTPInput({ 
  value, 
  onChange, 
  length = 4, 
  disabled = false, 
  className,
  dir = 'ltr',
  autoFocus = false,
  onComplete
}: OTPInputProps) {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Update internal state when value prop changes
  useEffect(() => {
    const newOtp = value.split('').slice(0, length);
    while (newOtp.length < length) {
      newOtp.push('');
    }
    setOtp(newOtp);
  }, [value, length]);

  const firstInputMounted = useRef(false);
  const setFirstInputRef = (el: HTMLInputElement | null) => {
    inputRefs.current[0] = el;
    if (el && autoFocus && !disabled && !firstInputMounted.current) {
      firstInputMounted.current = true;
      el.focus();
    }
  };

  // Check if OTP is complete and call onComplete
  useEffect(() => {
    const otpString = otp.join('');
    if (otpString.length === length && onComplete) {
      // Small delay to ensure the last input is processed
      setTimeout(() => {
        onComplete();
      }, 100);
    }
  }, [otp, length, onComplete]);

  const handleChange = (index: number, digit: string) => {
    const digits = digit.replace(/\D/g, '');

    // Multi-digit input (paste via onChange or auto-fill)
    if (digits.length > 1) {
      const newOtp = Array(length).fill('');
      for (let i = 0; i < Math.min(digits.length, length); i++) {
        newOtp[i] = digits[i];
      }
      setOtp(newOtp);
      onChange(newOtp.join(''));
      const focusIndex = Math.min(digits.length, length - 1);
      inputRefs.current[focusIndex]?.focus();
      return;
    }

    if (digits === '' && digit !== '') return;

    const newOtp = [...otp];
    newOtp[index] = digits;
    setOtp(newOtp);
    onChange(newOtp.join(''));

    if (digits !== '' && index < length - 1) {
      setTimeout(() => {
        inputRefs.current[index + 1]?.focus();
      }, 10);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
      // Move to previous input on backspace if current is empty
      const prevIndex = index - 1; // Always move to previous index
      if (prevIndex >= 0 && prevIndex < length) {
        setTimeout(() => {
          inputRefs.current[prevIndex]?.focus();
        }, 10);
      }
    } else if (e.key === 'Backspace' && otp[index] !== '') {
      // Clear current input on backspace
      handleChange(index, '');
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prevIndex = dir === 'rtl' ? index + 1 : index - 1;
      if (prevIndex >= 0 && prevIndex < length) {
        inputRefs.current[prevIndex]?.focus();
      }
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const nextIndex = dir === 'rtl' ? index - 1 : index + 1;
      if (nextIndex >= 0 && nextIndex < length) {
        inputRefs.current[nextIndex]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, length);
    
    if (digits.length > 0) {
      const newOtp = Array(length).fill('');
      for (let i = 0; i < digits.length; i++) {
        newOtp[i] = digits[i];
      }
      setOtp(newOtp);
      onChange(newOtp.join(''));
      
      // Focus the last filled input or the next empty one
      const focusIndex = Math.min(digits.length, length - 1);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  return (
    <div 
      className={cn("flex gap-2", dir === 'rtl' ? "flex-row-reverse" : "", className)}
      dir={dir}
    >
      {Array.from({ length }, (_, index) => (
        <Input
          key={index}
          ref={index === 0 ? setFirstInputRef : (el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={otp[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="w-12 h-12 text-center text-lg font-semibold border-2 focus:border-primary"
        />
      ))}
    </div>
  );
}